import { BreakPointCondition, useBreakpointIf, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { ScrollView as NBScrollView } from 'native-base';
import React, { useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useRecoilValue } from 'recoil';

import { ScrollCloseToBottomProvider } from '../../contexts/ScrollCloseToBottom';
import { useScrollCloseToBottom } from '../../hooks/useScrollCloseToBottom';
import {
  SignedInNavigationScreenNavigationProp,
  SignedInRootStackParamList,
} from '../../routes/types';
import { customerAssetProductSelected } from '../../store/customerDetail/atom';

import { AssetCard } from './components/AssetCard';
import { useHeightForAssetList } from './components/AssetTable/_dynamic/utils';
import { DynamicAssetTable } from './components/DynamicAsset';
import { Filters } from './components/Filters';
import { Header } from './components/Header';
import { useProductHasFilters } from './utils';

export const CustomerAssetDetail: React.FC<{ isInline?: boolean; marketType?: string }> = ({
  isInline,
  marketType,
}) => {
  const { params } = useRoute<RouteProp<SignedInRootStackParamList, 'CustomersAssetsDetails'>>();
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { isCloseToBottom, validateIsCloseToBottom } = useScrollCloseToBottom();
  const [currentData, setCurrentData] = React.useState<any[]>([]);

  const products = useRecoilValue(customerAssetProductSelected);
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const isTablet = useBreakpointIf(BreakPointCondition.EqualOrDown, 'md');
  const product = useMemo(
    () => products[marketType || params.marketType],
    [products, marketType, params.marketType]
  );

  const hasFilters = useProductHasFilters(product);
  const listHeight = useHeightForAssetList(currentData, isInline);

  const handleData = useCallback((data: any[]) => {
    setCurrentData(data);
  }, []);

  const handleCloseDetails = () => {
    navigate('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'CustomerDetail',
        params: { ...params, selectedTab: 'financialDetails' },
      },
    });
  };

  if (!product) {
    handleCloseDetails();
    return <></>;
  }

  return (
    <NBScrollView
      scrollEventThrottle={200}
      onScroll={({ nativeEvent }) => validateIsCloseToBottom(nativeEvent)}
      style={[styles.view, { maxHeight: listHeight, minHeight: listHeight }]}
      overflowY={isMobile || isTablet ? 'auto' : 'hidden'}>
      <ScrollCloseToBottomProvider isCloseToBottom={isCloseToBottom}>
        <VStack p={Spacing['inset.xs']} style={styles.view} overflow="auto">
          <VStack
            alignSelf="center"
            space={Spacing['stack.xxxs']}
            width="full"
            maxWidth={{ base: '100%', sm: '95%', lg: '1280px' }}
            minWidth={0}>
            {!isInline && <Header handleCloseDetails={handleCloseDetails} isMobile={isMobile} />}
            {!isInline && <AssetCard product={product} />}
            {hasFilters && <Filters product={product} isInline={isInline} />}
            <DynamicAssetTable
              product={product}
              productAtom={customerAssetProductSelected}
              onData={handleData}
            />
          </VStack>
        </VStack>
      </ScrollCloseToBottomProvider>
    </NBScrollView>
  );
};

const styles = StyleSheet.create({
  view: {
    backgroundColor: '#F9F9F9',
  },
});
